import { Button } from 'antd';
import { ListFilter } from 'lucide-react';
import { LIST_FILTERS_COPY } from '../copy';
import { LIST_FILTERS_ICON, LIST_FILTERS_LOOK } from '../listFiltersLook';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [LIST-FILTERS] THE "ADJUST FILTERS" BUTTON — ONE CONTROL, TWO STATES, NO OVERLAY OF ITS OWN.
 *
 *  ⚠️ THE VISIBLE TEXT IS THE ACCESSIBLE NAME — NO `aria-label`. "Adjust filters", or "Filters applied
 *  · 2" once something is on. An `aria-label` would replace the content and drift from it (WCAG
 *  2.5.3, `ColumnsMenu`'s own warning), and a test or a screen reader would hear a name nobody sees.
 *
 *  ⚠️ WHAT IS APPLIED IS A NATIVE `title`, ON THE APPLIED STATE ONLY — "Division: Central and North".
 *  A `title` is the accessible DESCRIPTION, so it adds to the name rather than replacing it; and it is
 *  never a `<Tooltip>`: `pages/Arrangements/index.tsx` is census-exempt at an exact Tooltip count, and
 *  a per-render rc-trigger is a measured cost (#82). This file holds no overlay at all.
 *
 *  ⚠️ `aria-haspopup="dialog"`, NEVER `aria-pressed`. It opens the dialog in both states; it is not a
 *  toggle, and the discard-mode button beside it is the toggle idiom on this header.
 *
 *  ⚠️ AN ANTD DEFAULT BUTTON WEARING `LIST_FILTERS_LOOK` — never `type="primary"` (it would outshout
 *  "New arrangement", the page's one primary), never `color=`/`variant=` (AntD's own presets, not
 *  tokens), and never `disabled`: the look would mask AntD's disabled styling. See `listFiltersLook.ts`.
 *
 *  ⚠️ ONE GLYPH IN BOTH STATES: `ListFilter`. `SlidersHorizontal` is Settings' and `Settings2` is the
 *  Columns menu one control to the right; a `FilterX` would read as "clear", and this opens the dialog.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const ListFiltersButton = ({
  activeCount,
  summary,
  onOpen,
}: {
  /** Every value selected, across every filter — `activeFilterCount`. `0` is the idle state. */
  activeCount: number;
  /** What is applied, in words — `listFiltersSummary`. Only read while something is applied. */
  summary: string | undefined;
  onOpen: () => void;
}) => {
  const applied = activeCount > 0;
  return (
    <Button
      className={LIST_FILTERS_LOOK[applied ? 'applied' : 'idle']}
      icon={<ListFilter className={LIST_FILTERS_ICON} aria-hidden />}
      aria-haspopup="dialog"
      title={applied ? summary : undefined}
      data-testid="list-filters"
      data-applied={applied ? 'true' : 'false'}
      data-count={String(activeCount)}
      onClick={onOpen}
    >
      {applied ? LIST_FILTERS_COPY.applied(activeCount) : LIST_FILTERS_COPY.adjust}
    </Button>
  );
};
